import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { usePipelinesSettings } from '@/hooks/usePipelinesSettings';
import { useTeam, type TeamMember } from '@/hooks/useTeam';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Switch } from '../ui/switch';
import { AlertCircle, CheckCircle2, Settings, Users, GripVertical } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import ScrollArea from '../ui/shared/ScrollArea';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface PipelineConfig {
  id?: string;
  distribution_type: 'manual' | 'equal';
  auto_assign: boolean;
  notify_on_assign: boolean;
}

interface PipelineMemberAssignment {
  id: string;
  user_id: string;
}

interface SortableDistributionMemberProps {
  member: TeamMember;
  isSelected: boolean;
  onToggle: (id: string) => void;
}

const SortableDistributionMember: React.FC<SortableDistributionMemberProps> = ({
  member,
  isSelected,
  onToggle,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: member.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 rounded-lg border border-[#3F3F464D] hover:bg-neutral-800/30 cursor-pointer"
      onClick={() => onToggle(member.id)}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing touch-none"
        aria-label="Arrastar para reordenar"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-5 h-5 text-neutral-500 hover:text-neutral-300" />
      </button>
      <Checkbox
        id={`member-${member.id}`}
        checked={isSelected}
        onCheckedChange={() => onToggle(member.id)}
        onClick={(e) => e.stopPropagation()}
      />
      <Label htmlFor={`member-${member.id}`} className="flex-1 cursor-pointer">
        <p className="font-medium text-neutral-50">{member.name || member.email}</p>
        {member.name && <p className="text-sm text-neutral-400">{member.email}</p>}
      </Label>
    </div>
  );
};

const DistributionTab: React.FC = () => {
  const { user } = useAuth();
  const { pipelines } = usePipelinesSettings();
  const { members, reorderMembers } = useTeam();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);
  const [pipelineConfigs, setPipelineConfigs] = useState<Record<string, PipelineConfig>>({});
  const [pipelineMembers, setPipelineMembers] = useState<Record<string, Set<string>>>({});
  const [formData, setFormData] = useState<PipelineConfig>({
    distribution_type: 'manual',
    auto_assign: false,
    notify_on_assign: true
  });
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [sortedMembers, setSortedMembers] = useState<TeamMember[]>([]);

  useEffect(() => {
    fetchAllConfigs();
  }, [pipelines]);

  useEffect(() => {
    setSortedMembers([...members].filter(m => m.is_active));
  }, [members]);

  const fetchAllConfigs = async () => {
    if (!user?.account?.id || !supabase || pipelines.length === 0) return;

    setLoading(true);
    try {
      const { data: configs } = await supabase
        .from('account_pipeline_distribution')
        .select('*')
        .eq('account_id', user.account.id);

      const { data: assignments } = await supabase
        .from('account_pipeline_members')
        .select('*')
        .eq('account_id', user.account.id)
        .eq('is_active', true);

      const configsMap: Record<string, PipelineConfig> = {};
      configs?.forEach(config => {
        configsMap[config.pipeline_id] = {
          id: config.id,
          distribution_type: config.distribution_type,
          auto_assign: config.auto_assign,
          notify_on_assign: config.notify_on_assign
        };
      });
      setPipelineConfigs(configsMap);

      const membersMap: Record<string, Set<string>> = {};
      assignments?.forEach(assignment => {
        if (!membersMap[assignment.pipeline_id]) {
          membersMap[assignment.pipeline_id] = new Set();
        }
        membersMap[assignment.pipeline_id].add(assignment.user_id);
      });
      setPipelineMembers(membersMap);

    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
    } finally {
      setLoading(false);
    }
  };

  const openConfigDialog = (pipelineId: string) => {
    setSelectedPipelineId(pipelineId);

    const config = pipelineConfigs[pipelineId];
    setFormData(config || {
      distribution_type: 'manual',
      auto_assign: false,
      notify_on_assign: true
    });

    const members = pipelineMembers[pipelineId] || new Set();
    setSelectedMembers(new Set(members));

    setDialogOpen(true);
  };

  const handleSaveConfig = async () => {
    if (!user?.account?.id || !selectedPipelineId || !supabase) return;

    setSaving(true);
    setSaveMessage(null);

    try {
      const existingConfig = pipelineConfigs[selectedPipelineId];

      if (existingConfig?.id) {
        const { error: updateError } = await supabase
          .from('account_pipeline_distribution')
          .update({
            distribution_type: formData.distribution_type,
            auto_assign: formData.auto_assign,
            notify_on_assign: formData.notify_on_assign,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingConfig.id);

        if (updateError) throw updateError;
      } else {
        const { data: newConfig, error: createError } = await supabase
          .from('account_pipeline_distribution')
          .insert({
            account_id: user.account.id,
            pipeline_id: selectedPipelineId,
            distribution_type: formData.distribution_type,
            auto_assign: formData.auto_assign,
            notify_on_assign: formData.notify_on_assign
          })
          .select()
          .single();

        if (createError) throw createError;
        formData.id = newConfig.id;
      }

      const { data: existingAssignments } = await supabase
        .from('account_pipeline_members')
        .select('id, user_id')
        .eq('account_id', user.account.id)
        .eq('pipeline_id', selectedPipelineId);

      const existingUserIds = new Set(existingAssignments?.map(a => a.user_id) || []);
      const toAdd = Array.from(selectedMembers).filter(id => !existingUserIds.has(id));
      const toRemove = existingAssignments?.filter(a => !selectedMembers.has(a.user_id)) || [];

      if (toAdd.length > 0) {
        const { error: insertError } = await supabase
          .from('account_pipeline_members')
          .insert(toAdd.map(userId => ({
            account_id: user.account.id,
            pipeline_id: selectedPipelineId,
            user_id: userId,
            is_active: true
          })));

        if (insertError) throw insertError;
      }

      if (toRemove.length > 0) {
        const { error: deleteError } = await supabase
          .from('account_pipeline_members')
          .delete()
          .in('id', toRemove.map(a => a.id));

        if (deleteError) throw deleteError;
      }

      setPipelineConfigs(prev => ({
        ...prev,
        [selectedPipelineId]: { ...formData }
      }));

      setPipelineMembers(prev => ({
        ...prev,
        [selectedPipelineId]: new Set(selectedMembers)
      }));

      setSaveMessage({ type: 'success', text: 'Configurações salvas com sucesso!' });

      setTimeout(() => {
        setDialogOpen(false);
        setSaveMessage(null);
      }, 1500);

    } catch (err: any) {
      setSaveMessage({ type: 'error', text: err.message || 'Erro ao salvar configurações' });
    } finally {
      setSaving(false);
    }
  };

  const toggleMember = (userId: string) => {
    setSelectedMembers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const toggleAllMembers = () => {
    const activeMembers = members.filter(m => m.is_active);
    if (selectedMembers.size === activeMembers.length) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(activeMembers.map(m => m.id)));
    }
  };

  const getDistributionTypeLabel = (type: string) => {
    return type === 'manual' ? 'Manual' : 'Distribuição Igual';
  };

  const getConfigSummary = (pipelineId: string) => {
    const config = pipelineConfigs[pipelineId];
    const memberCount = pipelineMembers[pipelineId]?.size || 0;

    if (!config) {
      return { type: 'Não configurado', members: '0 membros' };
    }

    return {
      type: getDistributionTypeLabel(config.distribution_type),
      members: `${memberCount} ${memberCount === 1 ? 'membro' : 'membros'}`
    };
  };

  const activeMembersCount = members.filter(m => m.is_active).length;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sortedMembers.findIndex((m) => m.id === active.id);
      const newIndex = sortedMembers.findIndex((m) => m.id === over.id);

      const newOrder = arrayMove(sortedMembers, oldIndex, newIndex);
      setSortedMembers(newOrder);
      await reorderMembers(newOrder);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-neutral-50">Distribuição por Pipeline</h2>
        <p className="text-sm text-neutral-400">Configure a distribuição de contatos para cada pipeline</p>
      </div>

      {loading ? (
        <div className="p-8 text-center text-neutral-400">Carregando...</div>
      ) : pipelines.length === 0 ? (
        <div className="p-8 text-center text-neutral-400">
          Nenhum pipeline encontrado. Crie um pipeline primeiro em Configurações → Pipelines
        </div>
      ) : (
        <div className="border border-[#3F3F464D] rounded-lg bg-[#18181B] divide-y divide-[#3F3F464D]">
          {pipelines.filter(p => p.is_active).map((pipeline) => {
            const summary = getConfigSummary(pipeline.id);
            return (
              <div key={pipeline.id} className="p-4 flex items-center justify-between hover:bg-neutral-800/30">
                <div className="flex-1">
                  <h3 className="font-medium text-neutral-50 mb-1">{pipeline.name}</h3>
                  <div className="flex items-center gap-4 text-sm text-neutral-400">
                    <span>{summary.type}</span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {summary.members}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={() => openConfigDialog(pipeline.id)}
                  className="bg-neutral-600 hover:bg-neutral-500 text-neutral-50"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configurar
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="flex w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col max-w-[600px]">
          <ScrollArea classNames={{ root: 'max-h-[90dvh] p-6 overflow-auto' }}>
            <DialogHeader>
              <DialogTitle className="text-white">
                Configurar Distribuição - {pipelines.find(p => p.id === selectedPipelineId)?.name}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 mt-6">
              <div className="space-y-4 p-4 border border-[#3F3F464D] rounded-lg">
                <h3 className="font-semibold text-neutral-50">Configurações de Distribuição</h3>

                <div className="flex flex-col gap-3">
                  <Label htmlFor="distribution_type">Tipo de Distribuição</Label>
                  <Select
                    value={formData.distribution_type}
                    onValueChange={(value: any) => setFormData({ ...formData, distribution_type: value })}
                  >
                    <SelectTrigger className="bg-neutral-600 border-[#3F3F46] text-neutral-50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-700 border-[#3F3F46]">
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="equal">Distribuição Igual</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="mt-2 space-y-2 text-sm text-neutral-400">
                    {formData.distribution_type === 'manual' && (
                      <div className="flex gap-2 items-start">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <p>Os contatos não serão distribuídos automaticamente. Você precisará atribuí-los manualmente.</p>
                      </div>
                    )}
                    {formData.distribution_type === 'equal' && (
                      <div className="flex gap-2 items-start">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <p>Os contatos serão distribuídos igualmente entre os membros selecionados abaixo.</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-4 border-t border-[#3F3F464D]">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="auto_assign">Atribuição Automática</Label>
                      <p className="text-sm text-neutral-400">
                        Atribuir automaticamente novos contatos
                      </p>
                    </div>
                    <Switch
                      id="auto_assign"
                      checked={formData.auto_assign}
                      onCheckedChange={(checked) => setFormData({ ...formData, auto_assign: checked })}
                      disabled={formData.distribution_type === 'manual'}
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-[#3F3F464D]">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="notify_on_assign">Notificar ao Atribuir</Label>
                      <p className="text-sm text-neutral-400">
                        Enviar notificação quando um contato for atribuído
                      </p>
                    </div>
                    <Switch
                      id="notify_on_assign"
                      checked={formData.notify_on_assign}
                      onCheckedChange={(checked) => setFormData({ ...formData, notify_on_assign: checked })}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4 p-4 border border-[#3F3F464D] rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-50">Membros da Equipe</h3>
                    <p className="text-sm text-neutral-400">
                      {selectedMembers.size} de {activeMembersCount} membros selecionados
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={toggleAllMembers}
                    className="text-sm text-blue-400 hover:text-blue-300"
                  >
                    {selectedMembers.size === activeMembersCount ? 'Desmarcar todas' : 'Selecionar todas'}
                  </button>
                </div>

                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {sortedMembers.length === 0 ? (
                    <div className="p-4 text-center text-neutral-400">Nenhum membro ativo na equipe</div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={sortedMembers.map(m => m.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {sortedMembers.map((member) => (
                          <SortableDistributionMember
                            key={member.id}
                            member={member}
                            isSelected={selectedMembers.has(member.id)}
                            onToggle={toggleMember}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              </div>

              {saveMessage && (
                <div
                  className={`flex items-center gap-2 p-4 rounded-lg ${
                    saveMessage.type === 'success'
                      ? 'bg-green-900/20 text-green-400 border border-green-800/30'
                      : 'bg-red-900/20 text-red-400 border border-red-800/30'
                  }`}
                >
                  {saveMessage.type === 'success' ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : (
                    <AlertCircle className="w-5 h-5" />
                  )}
                  <span>{saveMessage.text}</span>
                </div>
              )}

              <div className="flex gap-2 justify-end pt-4 border-t border-[#3F3F464D]">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  disabled={saving}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveConfig}
                  disabled={saving}
                  className="bg-[#E4E4E7] text-neutral-900 hover:bg-[#D4D4D8] min-w-[120px]"
                >
                  {saving ? 'Salvando...' : 'Salvar Configurações'}
                </Button>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DistributionTab;
